import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';
import { FaUndo, FaRedo, FaTimes, FaCalendarAlt, FaStickyNote, FaInfoCircle, FaTrash } from 'react-icons/fa';
import { daysService } from '../services/daysService';
import { isStructuredContent, getContentBlocksByType, createContentBlock, consolidateContentBlocks, validateContentBlock } from '../utils/contentBlockUtils';
import 'react-datepicker/dist/react-datepicker.css';
import './EditDayModal.css';

const EditDayModal = ({ isOpen, onClose, day, onSave, dayNumber, tripId, allDays }) => {
  const [editedDay, setEditedDay] = useState(day);
  const [textContent, setTextContent] = useState('');
  const [accommodationBlocks, setAccommodationBlocks] = useState([]);
  const [preservedBlocks, setPreservedBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAccommodationInfo, setShowAccommodationInfo] = useState(false);
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();

  useEffect(() => {
    console.log('📝 [EditDayModal] useEffect: Setting up day for editing', {
      dayId: day?.id,
      hasContent: !!day?.content,
      contentType: typeof day?.content,
      isStructured: isStructuredContent(day?.content)
    });

    let processedDay = { ...day };
    let textBlocks = [];
    let accomBlocks = [];

    if (day?.content && isStructuredContent(day.content)) {
      // Extract text and accommodation blocks separately
      textBlocks = getContentBlocksByType(day.content, 'text');
      accomBlocks = getContentBlocksByType(day.content, 'accommodation_reference');

      // Combine text blocks into single string for editing
      const textContent = textBlocks.map(block => block.text).join('\n');
      setTextContent(textContent);
      setAccommodationBlocks(accomBlocks);

      // Preserve all other blocks (documents, travel, etc.) so they're not lost
      const others = day.content.filter(block =>
        block.type !== 'text' && block.type !== 'accommodation_reference'
      );
      setPreservedBlocks(others);

      console.log('🔄 [EditDayModal] useEffect: Separated content blocks', {
        textBlockCount: textBlocks.length,
        accommodationBlockCount: accomBlocks.length,
        preservedBlockCount: others.length,
        textContentLength: textContent.length
      });
    } else if (typeof day?.content === 'string') {
      // Legacy string content - treat as text only
      setTextContent(day.content || '');
      setAccommodationBlocks([]);
      setPreservedBlocks([]);
    } else {
      // No content
      setTextContent('');
      setAccommodationBlocks([]);
      setPreservedBlocks([]);
    }

    setEditedDay(processedDay);
    setError('');
  }, [day]);

  const handleDateChange = (date) => {
    const adjustedDate = date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) : null;
    setEditedDay(prevDay => ({
      ...prevDay,
      date: adjustedDate ? adjustedDate.toISOString().split('T')[0] : null
    }));
  };

  const handleTextContentChange = (e) => {
    console.log('📝 [EditDayModal] handleTextContentChange: User typing text content', {
      newLength: e.target.value.length,
      isEmpty: e.target.value.trim() === ''
    });

    setTextContent(e.target.value);
  };

  const handleRemoveAccommodation = (accommodationId) => {
    console.log('🗑️ [EditDayModal] handleRemoveAccommodation: Removing accommodation', {
      accommodationId
    });

    setAccommodationBlocks(prev =>
      prev.filter(block => block.accommodationId !== accommodationId)
    );
  };

  const toggleAccommodationInfo = () => {
    setShowAccommodationInfo(prev => !prev);
  };

  const handleSave = async () => {
    console.log('💾 [EditDayModal] handleSave: Save button clicked');

    setLoading(true);
    setError('');
    try {
      // Build structured content from separate text and accommodation sections
      const contentBlocks = [];

      // Add text content block if there's text
      if (textContent.trim()) {
        contentBlocks.push(createContentBlock('text', {
          text: textContent.trim()
        }));
      }

      // Add accommodation blocks (preserving their metadata, adding default context if missing)
      contentBlocks.push(...accommodationBlocks.map(block => ({
        ...block,
        context: block.context || 'staying'
      })));

      // Add preserved blocks (documents, travel, etc.)
      contentBlocks.push(...preservedBlocks);

      // Consolidate content blocks to ensure proper structure
      const consolidatedContent = consolidateContentBlocks(contentBlocks);

      // Validate each content block before saving
      for (const block of consolidatedContent) {
        const validation = validateContentBlock(block);
        if (!validation.isValid) {
          console.error('❌ [EditDayModal] handleSave: Content block validation failed', {
            blockId: block.id,
            blockType: block.type,
            errors: validation.errors
          });
          setError(`Invalid content: ${validation.errors.join(', ')}`);
          setLoading(false);
          return;
        }
      }

      const dayUpdate = {
        ...editedDay,
        content: consolidatedContent
      };

      console.log('📊 [EditDayModal] handleSave: Saving structured content', {
        dayId: editedDay.id,
        textBlockCount: consolidatedContent.filter(b => b.type === 'text').length,
        accommodationBlockCount: consolidatedContent.filter(b => b.type === 'accommodation_reference').length,
        otherBlockCount: consolidatedContent.filter(b => b.type !== 'text' && b.type !== 'accommodation_reference').length,
        totalBlocks: consolidatedContent.length
      });

      const updatedDays = await daysService.updateDayAndAdjustSequence(
        effectiveUserId,
        tripId,
        editedDay.id,
        dayUpdate,
        allDays
      );
      console.log('✅ [EditDayModal] handleSave: Update successful, result count:', updatedDays?.length);
      onSave(updatedDays);
    } catch (err) {
      setError('Failed to update day. Please try again.');
      console.error('❌ [EditDayModal] handleSave: Error updating day:', err);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel={`Edit Day ${dayNumber}`}
      className="modal"
    >
      <div className="modal-content">
        <h2>Edit Day {dayNumber}</h2>
        {error && <p className="error">{error}</p>}
        <div className="form-group">
          <label>Date</label>
          <DatePicker
            selected={editedDay?.date ? new Date(editedDay.date) : null}
            onChange={handleDateChange}
            dateFormat="EEE dd MMM yyyy"
            isClearable
            disabled={loading}
            className="date-picker"
          />
        </div>
        <div className="form-group">
          <label>Text Content</label>
          <textarea
            value={textContent}
            onChange={handleTextContentChange}
            placeholder="Day content (activities, notes, etc.)\n\nNote: Keep text concise for day list display"
            disabled={loading}
            className="content-input"
            rows={4}
          />
          {textContent.length > 200 && (
            <div className="content-warning">
              ⚠️ Long content may be truncated in day list view
            </div>
          )}
        </div>

        {accommodationBlocks.length > 0 && (
          <div className="form-group">
            <div className="accommodation-header">
              <label>Accommodation Reference</label>
              <FaInfoCircle
                className="info-icon"
                onClick={toggleAccommodationInfo}
                title="Click for accommodation management info"
              />
            </div>
            {accommodationBlocks.map((block) => (
              <div key={block.id} className="accommodation-reference">
                <span className="accommodation-text">{block.displayText}</span>
                <FaTrash
                  className="delete-icon"
                  onClick={() => handleRemoveAccommodation(block.accommodationId)}
                  title="Remove accommodation from this day"
                />
              </div>
            ))}

            {showAccommodationInfo && (
              <div className="accommodation-info-popup">
                <div className="info-content">
                  <h4>Managing Accommodations</h4>
                  <p>To modify accommodation details:</p>
                  <ul>
                    <li>Go to the Accommodation section</li>
                    <li>Edit the accommodation details there</li>
                    <li>Changes will automatically appear here</li>
                  </ul>
                  <p>To remove: Click the trash icon above</p>
                  <button
                    onClick={toggleAccommodationInfo}
                    className="close-info-btn"
                  >
                    Got it
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="modal-buttons">
          <button onClick={handleSave} disabled={loading} className="primary">
            {loading ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose} disabled={loading} className="secondary">Cancel</button>
        </div>
      </div>
    </Modal>
  );
};

export default EditDayModal;